import React, { useState } from 'react';
import { Calendar, Users, Mail, Phone, MessageSquare, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

// ─── EmailJS config réservations ────────────────────────────
const SERVICE_ADMIN      = 'service_nd00b6k';   // service admin + client
const TEMPLATE_ADMIN     = 'template_fbauv0r';  // tu reçois la demande
const TEMPLATE_CLIENT    = 'template_p0qhygq';  // le client reçoit confirmation
const PUBLIC_KEY_RESERVATIONS = 'ii6G0UP7KxEHmS5gq'; // service_nd00b6k

const BookingForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    event_type: '',
    guests: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, event_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const params = {
          from_name:  formData.name,
          from_email: formData.email,
          phone:      formData.phone,
          date:       formData.date,
          event_type: formData.event_type,
          guests:     formData.guests,
          message:    formData.message || '—',
          year:       new Date().getFullYear(),
        };

      // Email 1 → toi (admin)
      await emailjs.send(SERVICE_ADMIN, TEMPLATE_ADMIN, params, PUBLIC_KEY_RESERVATIONS);
      // Email 2 → le client (confirmation)
      await emailjs.send(SERVICE_ADMIN, TEMPLATE_CLIENT, params, PUBLIC_KEY_RESERVATIONS);

      toast.success("Demande envoyée !", {
        description: "Nous vous recontacterons très prochainement par email.",
      });
      setFormData({ name: '', email: '', phone: '', date: '', event_type: '', guests: '', message: '' });
    } catch (error) {
      console.error('EmailJS error:', error);
      toast.error("Erreur", {
        description: "Une erreur est survenue. Veuillez réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-burgundy/5 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold text-charcoal mb-4">
            Réserver votre Événement
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto mb-6"></div>
          <p className="text-lg text-charcoal/70 max-w-2xl mx-auto">
            Remplissez le formulaire ci-dessous et nous vous recontacterons rapidement
          </p>
        </div>

        <Card className="border-none shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-gold/10 to-burgundy/10 border-b border-gold/20">
            <CardTitle className="text-2xl text-charcoal">Formulaire de Réservation</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-charcoal font-semibold">Nom complet *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Votre nom" required className="border-charcoal/20 focus:border-gold" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-charcoal font-semibold">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal/40 w-5 h-5" />
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="votre@email.com" required className="pl-10 border-charcoal/20 focus:border-gold" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-charcoal font-semibold">Téléphone *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal/40 w-5 h-5" />
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="06 12 34 56 78" required className="pl-10 border-charcoal/20 focus:border-gold" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-charcoal font-semibold">Date souhaitée *</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal/40 w-5 h-5" />
                    <Input id="date" name="date" type="date" value={formData.date} onChange={handleChange} required className="pl-10 border-charcoal/20 focus:border-gold" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="eventType" className="text-charcoal font-semibold">Type d'événement *</Label>
                  <Select onValueChange={handleSelectChange} value={formData.event_type} required>
                    <SelectTrigger className="border-charcoal/20 focus:border-gold">
                      <SelectValue placeholder="Sélectionnez un type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mariage">Mariage</SelectItem>
                      <SelectItem value="anniversaire">Anniversaire</SelectItem>
                      <SelectItem value="bapteme">Baptême</SelectItem>
                      <SelectItem value="entreprise">Evénement d'entreprise</SelectItem>
                      <SelectItem value="cocktail">Cocktail</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guests" className="text-charcoal font-semibold">Nombre de personnes *</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal/40 w-5 h-5" />
                    <Input id="guests" name="guests" type="number" min="1" value={formData.guests} onChange={handleChange} placeholder="Ex: 50" required className="pl-10 border-charcoal/20 focus:border-gold" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-charcoal font-semibold">Message</Label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-charcoal/40 w-5 h-5" />
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Dites-nous en plus sur votre événement, vos préférences culinaires, contraintes alimentaires..." rows={5} className="pl-10 pt-3 border-charcoal/20 focus:border-gold resize-none" />
                </div>
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full bg-gold hover:bg-gold/90 text-charcoal font-semibold py-6 text-lg transition-all duration-200 hover:shadow-lg disabled:opacity-50">
                {isSubmitting ? <span>Envoi en cours...</span> : <><Send className="w-5 h-5 mr-2" />Envoyer ma demande</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default BookingForm;
